import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import FloatingSymbols from '../components/FloatingSymbols';
import BrandLogo from '../components/BrandLogo';

const Register = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    age: '', gender: '', location: '', vintage_years: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const upd = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setLoading(true);
    const result = await register(form.name, form.email, form.password, form.phone, {
      age: form.age ? parseInt(form.age, 10) : null,
      gender: form.gender || null,
      location: form.location || null,
      vintage_years: form.vintage_years ? parseFloat(form.vintage_years) : null,
    });
    if (result.success) { toast.success('Account created successfully!'); navigate('/dashboard'); }
    else toast.error(result.error || 'Registration failed');
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 overflow-hidden">
      <FloatingSymbols opacity={0.9} />
      <div className="relative z-10 w-full max-w-2xl">
        <div className="text-center mb-6">
          <BrandLogo size={44} className="mb-2 justify-center w-full" />
          <p className="text-slate-600">Create your agent account</p>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>Sign Up</CardTitle>
            <CardDescription>Tell us a bit about your practice so we can tailor the CRM.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => upd({ name: e.target.value })} required data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => upd({ email: e.target.value })} required data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => upd({ phone: e.target.value })} data-testid="register-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input id="age" type="number" min="18" max="99" value={form.age} onChange={(e) => upd({ age: e.target.value })} data-testid="register-age-input" />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={(v) => upd({ gender: v })}>
                    <SelectTrigger data-testid="register-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location (City, State)</Label>
                  <Input id="location" placeholder="Mumbai, Maharashtra" value={form.location} onChange={(e) => upd({ location: e.target.value })} data-testid="register-location-input" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="vintage_years">Vintage — years as an insurance professional</Label>
                  <Input id="vintage_years" type="number" step="0.5" min="0" value={form.vintage_years} onChange={(e) => upd({ vintage_years: e.target.value })} placeholder="e.g. 3.5" data-testid="register-vintage-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input id="password" type="password" value={form.password} onChange={(e) => upd({ password: e.target.value })} required data-testid="register-password-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input id="confirmPassword" type="password" value={form.confirmPassword} onChange={(e) => upd({ confirmPassword: e.target.value })} required data-testid="register-confirm-password-input" />
                </div>
              </div>

              <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900 text-white font-medium transition-colors duration-200" disabled={loading} data-testid="register-submit-button">
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="spinner mr-2" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <UserPlus className="mr-2 h-4 w-4" /> Create Account
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              <span className="text-slate-600">Already have an account? </span>
              <Link to="/login" className="text-blue-800 hover:text-blue-900 font-medium" data-testid="login-link">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
